import React, { useState, useEffect } from "react";
import useTVStore from "../store/tvStore.jsx";
import useAuthStore from "../store/authStore.jsx";
import {
  useTVs,
  useUpdateTV,
  useDeleteTVMulti,
} from "../hooks/useTVQueries.jsx";
import { useUpdateTVEnable } from "../hooks/useTVQueries.jsx";
import { tvApi } from "../api/services.jsx";
import TVManagement from "../components/TVManagement.jsx";
import ContentManagement from "../components/ContentManagement.jsx";
import MusicManagement from "../components/MusicManagement.jsx";
import SettingsManagement from "../components/SettingsManagement.jsx";
import PinManagement from "../components/PinManagement.jsx";
import SampleDataLoader from "../components/SampleDataLoader.jsx";
import ConfirmationModal from "../components/ConfirmationModal.jsx";
import { useToast } from "../context/ToastContext.jsx";

const AdminPanel = () => {
  const [selectedTVId, setSelectedTVId] = useState(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [pendingEnabledState, setPendingEnabledState] = useState(null);
  const [currentTVData, setCurrentTVData] = useState(null);
  const [isLoadingTVData, setIsLoadingTVData] = useState(false);
  const [isSavingAll, setIsSavingAll] = useState(false);
  const [isSampleDataLoaded, setIsSampleDataLoaded] = useState(false);
  const [showDataClearModal, setShowDataClearModal] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);

  // 현황판 삭제 관련 상태 추가
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingTV, setIsDeletingTV] = useState(false);

  // 브라우저 상태 관리 - 각 섹션의 변경사항 추적
  const [bgmSettings, setBgmSettings] = useState({
    enabled: false,
    volume: 50,
  });

  // 각 섹션의 데이터 상태
  const [currentContents, setCurrentContents] = useState([]);
  const [currentMusic, setCurrentMusic] = useState([]);
  const [currentSettings, setCurrentSettings] = useState({
    transitionDuration: 1,
    transitionStyle: "fade",
  });
  const [currentPinSettings, setCurrentPinSettings] = useState({
    enabled: false,
    code: "",
  });

  // Zustand 상태
  const { tvs, currentTV, setCurrentTV } = useTVStore();

  // 인증 상태
  const { user, logout } = useAuthStore();

  // Toast 훅
  const { toastSuccess, toastError } = useToast();

  // React Query 훅들
  const {
    data: tvsData,
    isLoading: tvsLoading,
    refetch: refetchTVs,
  } = useTVs();

  // TV 수정 mutation 훅 사용
  const updateTVMutation = useUpdateTV();

  // TV 활성화/비활성화 훅
  const updateTVEnableMutation = useUpdateTVEnable();

  // TV 삭제 mutation 훅 추가
  const deleteTVMutation = useDeleteTVMulti();

  // 새로 추가: 마우스 커서 로딩 스피너 효과
  useEffect(() => {
    if (isLoadingTVData) {
      // 로딩 중일 때 body에 클래스 추가
      document.body.classList.add("tv-data-loading");

      // 동적으로 CSS 스타일 추가
      const style = document.createElement("style");
      style.id = "cursor-loading-style";
      style.textContent = `
        .tv-data-loading {
          cursor: wait !important;
        }
        
        .tv-data-loading * {
          cursor: wait !important;
        }
        
        .tv-data-loading::after {
          content: '';
          position: fixed;
          top: 0;
          left: 0;
          width: 20px;
          height: 20px;
          border: 2px solid #f3f3f3;
          border-top: 2px solid #3498db;
          border-radius: 50%;
          animation: cursor-spin 1s linear infinite;
          pointer-events: none;
          z-index: 10000;
          transform: translate(var(--mouse-x, 0px), var(--mouse-y, 0px)) translate(20px, -10px);
        }
        
        @keyframes cursor-spin {
          0% { transform: translate(var(--mouse-x, 0px), var(--mouse-y, 0px)) translate(20px, -10px) rotate(0deg); }
          100% { transform: translate(var(--mouse-x, 0px), var(--mouse-y, 0px)) translate(20px, -10px) rotate(360deg); }
        }
      `;
      document.head.appendChild(style);

      // 마우스 위치 추적
      const handleMouseMove = (e) => {
        document.documentElement.style.setProperty(
          "--mouse-x",
          e.clientX + "px"
        );
        document.documentElement.style.setProperty(
          "--mouse-y",
          e.clientY + "px"
        );
      };

      document.addEventListener("mousemove", handleMouseMove);

      // 클린업 함수
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
      };
    } else {
      // 로딩이 끝나면 클래스와 스타일 제거
      document.body.classList.remove("tv-data-loading");
      const existingStyle = document.getElementById("cursor-loading-style");
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    }
  }, [isLoadingTVData]);

  // 수정된 함수: TV 데이터 조회 시 모든 섹션 상태 초기화 - tvName으로 조회
  const fetchTVData = async (tvId) => {
    if (!tvId) return;

    setIsLoadingTVData(true);
    try {
      // tvs 배열에서 해당 tvId의 name 찾기
      const targetTv = tvsData?.find((tv) => tv.id === tvId);
      if (!targetTv || !targetTv.name) {
        throw new Error("TV 정보를 찾을 수 없습니다.");
      }

      // TVManagement와 동일하게 tvName으로 조회
      const response = await tvApi.getTvPlay(targetTv.name);
      if (response.success && response.data) {
        const tvData = response.data;
        setCurrentTVData(tvData);
        setCurrentTV(tvData);

        // 각 섹션 상태 초기화
        setBgmSettings({
          enabled: tvData.isBgmEnabled || false,
          volume: tvData.bgmMappings?.[0]?.volume || 50,
        });

        setCurrentContents(tvData.contentMappings || []);
        setCurrentMusic(tvData.bgmMappings || []);
        setCurrentSettings({
          transitionDuration: tvData.transitionDuration || 1,
          transitionStyle: tvData.transitionType || "fade",
        });
        setCurrentPinSettings({
          enabled: !!tvData.authenticationCode,
          code: tvData.authenticationCode || "",
        });
      }
    } catch (error) {
      toastError("TV 정보를 불러오는데 실패했습니다.");
    } finally {
      setIsLoadingTVData(false);
    }
  };

  // 수정된 함수: TV 선택 시 모든 상태 리셋
  const handleTVSelect = (tvId) => {
    setSelectedTVId(tvId);
    setIsSampleDataLoaded(false);

    // 모든 섹션 상태 리셋
    setBgmSettings({ enabled: false, volume: 50 });
    setCurrentContents([]);
    setCurrentMusic([]);
    setCurrentSettings({ transitionDuration: 1, transitionStyle: "fade" });
    setCurrentPinSettings({ enabled: false, code: "" });

    fetchTVData(tvId);
  };

  // TV 활성화/비활성화 토글 (확인 모달 표시)
  const handleToggleEnabled = (enabled) => {
    setPendingEnabledState(enabled);
    setShowEnableModal(true);
  };

  // 실제 TV 활성화/비활성화 처리
  const handleConfirmToggleEnabled = async () => {
    if (!currentTVData || pendingEnabledState === null) return;

    try {
      await updateTVEnableMutation.mutateAsync({
        tvId: currentTVData.id,
        enabled: pendingEnabledState,
      });

      // 로컬 상태 업데이트
      const updatedTVData = {
        ...currentTVData,
        enabled: pendingEnabledState,
      };
      setCurrentTVData(updatedTVData);
      setCurrentTV(updatedTVData);

      toastSuccess(
        pendingEnabledState
          ? `${
              currentTVData.name || currentTVData.id
            } 현황판이 활성화되었습니다.`
          : `${
              currentTVData.name || currentTVData.id
            } 현황판이 비활성화되었습니다.`
      );
    } catch (error) {
      toastError("현황판 상태 변경에 실패했습니다.");
    } finally {
      setShowEnableModal(false);
      setPendingEnabledState(null);
    }
  };

  // 활성화 변경 취소
  const handleCancelToggleEnabled = () => {
    setShowEnableModal(false);
    setPendingEnabledState(null);
  };

  // 각 섹션의 변경사항을 받는 콜백 함수들
  const handleBgmSettingsChange = (newSettings) => {
    setBgmSettings(newSettings);
  };

  const handleContentsChange = (newContents) => {
    setCurrentContents(newContents);
  };

  const handleMusicChange = (newMusic) => {
    setCurrentMusic(newMusic);
  };

  const handleSettingsChange = (newSettings) => {
    setCurrentSettings(newSettings);
  };

  const handlePinSettingsChange = (newPinSettings) => {
    setCurrentPinSettings(newPinSettings);
  };

  // 수정된 함수: 샘플 데이터 로드 시 모든 섹션 상태 업데이트
  const handleSampleDataLoad = (sampleTVData) => {
    setCurrentTVData(sampleTVData);
    setCurrentTV(sampleTVData);
    setIsSampleDataLoaded(true);

    // 샘플 데이터로 모든 섹션 상태 업데이트
    setBgmSettings({
      enabled: true,
      volume: 50,
    });

    setCurrentContents(sampleTVData.contentMappings || []);
    setCurrentMusic(sampleTVData.bgmMappings || []);
    // PIN과 설정은 기존 값 유지
  };

  // 수정된 함수: 설정 저장 - 각 컴포넌트의 변환 함수 사용
  // AdminPanel.jsx의 handleSaveSettings 함수 수정
  const handleSaveSettings = async () => {
    if (!currentTVData) {
      toastError("저장할 현황판이 선택되지 않았습니다.");
      return;
    }
    if (!currentPinSettings.code || !currentPinSettings.code.trim()) {
      toastError("PIN을 설정해주세요. PIN은 현황판 접근을 위해 필수입니다.");
      return;
    }

    const pinPattern = /^\d{6}$/;
    if (!pinPattern.test(currentPinSettings.code.trim())) {
      toastError("PIN은 6자리 숫자여야 합니다.");
      return;
    }

    setIsSavingAll(true);
    try {
      // 1단계: 기본 설정만 먼저 저장
      const basicUpdateData = {
        pin: currentPinSettings.enabled ? currentPinSettings.code : null,
        isBgmEnabled: bgmSettings.enabled,
        transitionType: getTransitionTypeEnum(currentSettings.transitionStyle),
        transitionDuration: currentSettings.transitionDuration || 1,
        bgmMappings: [],
        contentMappings: [],
      };

      await updateTVMutation.mutateAsync({
        tvId: currentTVData.id,
        tvData: basicUpdateData,
      });

      // 2단계: 작은 크기의 컨텐츠/음악만 있으면 함께 저장
      const musicData = MusicManagement.convertMusicListForServer
        ? MusicManagement.convertMusicListForServer(currentMusic)
        : [];

      const contentData = ContentManagement.convertContentListForServer
        ? ContentManagement.convertContentListForServer(currentContents)
        : [];

      // 데이터 크기 체크
      const musicSize = new Blob([JSON.stringify(musicData)]).size;
      const contentSize = new Blob([JSON.stringify(contentData)]).size;
      const totalSize = musicSize + contentSize;

      if (totalSize < 5 * 1024 * 1024) {
        // 5MB 이하면 함께 전송
        const fullUpdateData = {
          ...basicUpdateData,
          bgmMappings: musicData,
          contentMappings: contentData,
        };

        await updateTVMutation.mutateAsync({
          tvId: currentTVData.id,
          tvData: fullUpdateData,
        });
      } else {
        // 크기가 크면 순차적으로 저장
        if (musicData.length > 0) {
          const musicUpdateData = {
            ...basicUpdateData,
            bgmMappings: musicData,
          };
          await updateTVMutation.mutateAsync({
            tvId: currentTVData.id,
            tvData: musicUpdateData,
          });
        }

        if (contentData.length > 0) {
          const contentUpdateData = {
            ...basicUpdateData,
            contentMappings: contentData,
          };
          await updateTVMutation.mutateAsync({
            tvId: currentTVData.id,
            tvData: contentUpdateData,
          });
        }
      }

      toastSuccess("설정이 저장되었습니다.");
      setIsSampleDataLoaded(false);
      await fetchTVData(selectedTVId);
    } catch (error) {
      // 413 에러인 경우 특별한 메시지
      if (
        error.message.includes("413") ||
        error.message.includes("Entity Too Large")
      ) {
        toastError(
          "파일 크기가 너무 큽니다. 더 작은 파일을 사용하거나 관리자에게 문의하세요."
        );
      } else {
        toastError(`설정 저장 실패: ${error.message}`);
      }
    } finally {
      setIsSavingAll(false);
    }
  };

  // 데이터 초기화 버튼 클릭 시 확인 모달 표시
  const handleDataClear = () => {
    setShowDataClearModal(true);
  };

  // 실제 데이터 초기화 처리
  const handleConfirmDataClear = async () => {
    if (!currentTVData) {
      toastError("초기화할 현황판이 선택되지 않았습니다.");
      return;
    }

    setIsClearingData(true);
    try {
      // 배경음악과 컨텐츠만 빈값으로, 나머지 설정은 그대로 유지
      const updateData = {
        // PIN 설정 - 현재 설정 유지
        pin: currentPinSettings.enabled ? currentPinSettings.code : null,

        // 배경음악 사용여부 - 현재 설정 유지
        isBgmEnabled: bgmSettings.enabled,

        // 전환 효과 설정 - 현재 설정 유지
        transitionType: getTransitionTypeEnum(currentSettings.transitionStyle),

        // 전환 시간 - 현재 설정 유지
        transitionDuration: currentSettings.transitionDuration || 1,

        // 배경음악 매핑 - 빈 배열로 초기화
        bgmMappings: [],

        // 컨텐츠 매핑 - 빈 배열로 초기화
        contentMappings: [],
      };

      // React Query mutation 사용
      await updateTVMutation.mutateAsync({
        tvId: currentTVData.id,
        tvData: updateData,
      });

      toastSuccess("데이터가 초기화되었습니다.");
      setIsSampleDataLoaded(false); // 초기화 후 샘플 데이터 상태 리셋

      // 로컬 상태도 초기화
      setCurrentContents([]);
      setCurrentMusic([]);

      // 데이터 새로고침
      await fetchTVData(selectedTVId);
    } catch (error) {
      toastError(`데이터 초기화 실패: ${error.message}`);
    } finally {
      setIsClearingData(false);
      setShowDataClearModal(false);
    }
  };

  // 데이터 초기화 취소
  const handleCancelDataClear = () => {
    setShowDataClearModal(false);
  };

  // 현황판 삭제 버튼 클릭 시 확인 모달 표시
  const handleDeleteTV = () => {
    setShowDeleteModal(true);
  };

  // 실제 현황판 삭제 처리
  const handleConfirmDeleteTV = async () => {
    if (!currentTVData) {
      toastError("삭제할 현황판이 선택되지 않았습니다.");
      return;
    }

    setIsDeletingTV(true);
    try {
      // 단일 TV ID를 배열로 감싸서 전송 (다중 삭제 API 활용)
      await deleteTVMutation.mutateAsync([currentTVData.id]);

      toastSuccess(
        `${currentTVData.name || currentTVData.id} 현황판이 삭제되었습니다.`
      );

      // 삭제 후 상태 리셋
      setSelectedTVId(null);
      setCurrentTVData(null);
      setCurrentTV(null);
      setIsSampleDataLoaded(false);

      // 모든 섹션 상태 리셋
      setBgmSettings({ enabled: false, volume: 50 });
      setCurrentContents([]);
      setCurrentMusic([]);
      setCurrentSettings({ transitionDuration: 1, transitionStyle: "fade" });
      setCurrentPinSettings({ enabled: false, code: "" });

      // TV 목록 새로고침
      refetchTVs();
    } catch (error) {
      toastError(`현황판 삭제 실패: ${error.message}`);
    } finally {
      setIsDeletingTV(false);
      setShowDeleteModal(false);
    }
  };

  // 현황판 삭제 취소
  const handleCancelDeleteTV = () => {
    setShowDeleteModal(false);
  };

  // Enum 변환 함수들 - 백엔드 스펙에 맞게 수정
  const getTransitionTypeEnum = (type) => {
    const enumMap = {
      fade: "FADE",
      slideLeft: "LEFT_SLIDE",
      slideRight: "RIGHT_SLIDE",
      slideUp: "UP_SLIDE",
      slideDown: "DOWN_SLIDE",
      zoomIn: "ZOOM_IN",
      zoomOut: "ZOOM_OUT",
      rotateLeft: "ROTATE_LEFT",
      rotateRight: "ROTATE_RIGHT",
      flipHorizontal: "HORIZONTAL_FLIP",
      flipVertical: "VERTICAL_FLIP",
      blinds: "BLIND",
      cube: "CUBE_ROTATION",
    };
    return enumMap[type] || "FADE";
  };

  const getContentTypeEnum = (type) => {
    switch (type) {
      case "image":
        return "IMAGE";
      case "video":
        return "VIDEO";
      case "website":
        return "WEBSITE";
      default:
        return "IMAGE";
    }
  };

  // 수정된 함수: 데이터 새로고침 시 모든 상태 리셋
  const handleRefresh = () => {
    refetchTVs();
    setIsSampleDataLoaded(false);

    // 모든 섹션 상태 리셋
    setBgmSettings({ enabled: false, volume: 50 });
    setCurrentContents([]);
    setCurrentMusic([]);
    setCurrentSettings({ transitionDuration: 1, transitionStyle: "fade" });
    setCurrentPinSettings({ enabled: false, code: "" });

    if (selectedTVId) {
      fetchTVData(selectedTVId);
    }
  };

  // 로그아웃 버튼 클릭 시 확인 모달 표시
  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  // 실제 로그아웃 처리
  const handleConfirmLogout = async () => {
    try {
      await logout();
      setShowLogoutModal(false);
    } catch (error) {
      setShowLogoutModal(false);
    }
  };

  // 로그아웃 취소
  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        padding: "20px",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            color: "white",
            padding: "20px 30px",
            position: "relative",
          }}
        >
          {/* 왼쪽 상단 - 현황판 리스트 버튼 */}
          <div
            style={{
              position: "absolute",
              top: "20px",
              left: "30px",
              display: "flex",
              alignItems: "center",
              gap: "15px",
            }}
          >
            <button
              onClick={() => (window.location.href = "/")}
              style={{
                background: "rgba(255,255,255,0.2)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                backdropFilter: "blur(10px)",
              }}
              onMouseOver={(e) =>
                (e.target.style.background = "rgba(255,255,255,0.3)")
              }
              onMouseOut={(e) =>
                (e.target.style.background = "rgba(255,255,255,0.2)")
              }
            >
              📋 현황판 리스트
            </button>
          </div>

          {/* 오른쪽 상단 - 로그아웃 버튼과 네비게이션을 오른쪽 상단에 절대 위치 */}
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "30px",
              display: "flex",
              alignItems: "center",
              gap: "15px",
            }}
          >
            {user && (
              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "13px",
                    opacity: 0.9,
                    marginBottom: "2px",
                  }}
                >
                  <strong>{user.name || user.signInId}</strong>님
                </div>
              </div>
            )}

            {/* 관리자 관리 이동 버튼 (슬퍼 관리자만) */}
            {user && user.roles && user.roles.includes("SUPER") && (
              <button
                onClick={() => (window.location.href = "/admin/users")}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  color: "white",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "6px",
                  padding: "6px 12px",
                  fontSize: "12px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  backdropFilter: "blur(10px)",
                  marginRight: "8px",
                }}
                onMouseOver={(e) =>
                  (e.target.style.background = "rgba(255,255,255,0.3)")
                }
                onMouseOut={(e) =>
                  (e.target.style.background = "rgba(255,255,255,0.2)")
                }
              >
                👥 관리자 설정
              </button>
            )}

            <button
              onClick={handleLogoutClick}
              style={{
                background: "rgba(255,255,255,0.2)",
                color: "white",
                border: "1px solid rgba(255,255,255,0.3)",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "12px",
                cursor: "pointer",
                transition: "all 0.2s",
                backdropFilter: "blur(10px)",
              }}
              onMouseOver={(e) =>
                (e.target.style.background = "rgba(255,255,255,0.3)")
              }
              onMouseOut={(e) =>
                (e.target.style.background = "rgba(255,255,255,0.2)")
              }
            >
              로그아웃
            </button>
          </div>

          {/* 중앙 정렬된 제목 */}
          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                margin: "0 0 5px 0",
                fontSize: "24px",
                fontWeight: "600",
              }}
            >
              📺 현황판 관리 시스템
            </h1>
            <p
              style={{
                margin: 0,
                fontSize: "14px",
                opacity: 0.9,
              }}
            >
              TV 현황판을 설정하고 관리하세요
            </p>
          </div>
        </div>

        {/* 내용 영역 */}
        <div
          style={{
            padding: "20px 30px",
            background: "white",
            borderRadius: "0 0 12px 12px",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          {/* TV 관리 섹션 */}
          <div style={{ marginBottom: "20px" }}>
            <TVManagement
              tvs={tvsData || []}
              selectedTVId={selectedTVId}
              onTVSelect={handleTVSelect}
              isLoading={tvsLoading}
              isAdminMode={true}
            />
          </div>

          {/* 선택된 TV가 있을 때만 컨텐츠 관리 표시 */}
          {selectedTVId && currentTVData && (
            <>
              {/* TV 정보 - 컴팩트한 노란색 카드 */}
              <div
                style={{
                  marginBottom: "20px",
                  background: "linear-gradient(135deg, #fff9c4, #fef3c7)",
                  border: "1px solid #fbbf24",
                  borderRadius: "12px",
                  padding: "16px 20px",
                }}
              >
                {/* 헤더 */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                  }}
                >
                  <h3
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      margin: 0,
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#92400e",
                    }}
                  >
                    <span>📺</span>
                    현재 선택된 현황판
                  </h3>

                  {/* 활성화/비활성화 토글 */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "12px",
                        color: currentTVData.enabled ? "#1976d2" : "#666",
                        fontWeight: "500",
                      }}
                    >
                      활성화여부
                    </span>
                    <label
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: "44px",
                        height: "24px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={currentTVData.enabled || false}
                        onChange={(e) => handleToggleEnabled(e.target.checked)}
                        disabled={updateTVEnableMutation.isPending}
                        style={{
                          opacity: 0,
                          width: 0,
                          height: 0,
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          cursor: updateTVEnableMutation.isPending
                            ? "wait"
                            : "pointer",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: currentTVData.enabled
                            ? "#1976d2"
                            : "#ccc",
                          transition: "0.3s",
                          borderRadius: "24px",
                        }}
                      >
                        <span
                          style={{
                            position: "absolute",
                            content: '""',
                            height: "18px",
                            width: "18px",
                            left: currentTVData.enabled ? "23px" : "3px",
                            bottom: "3px",
                            backgroundColor: "white",
                            transition: "0.3s",
                            borderRadius: "50%",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                          }}
                        />
                      </span>
                    </label>
                  </div>
                </div>

                {/* 샘플 데이터 로드 상태 표시 */}
                {isSampleDataLoaded && (
                  <div
                    style={{
                      marginBottom: "12px",
                      padding: "8px 12px",
                      background: "linear-gradient(45deg, #fef3c7, #fde68a)",
                      border: "1px solid #f59e0b",
                      borderRadius: "8px",
                      fontSize: "12px",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span style={{ fontSize: "14px" }}>🎯</span>
                    <span style={{ color: "#92400e", fontWeight: "500" }}>
                      <strong>샘플 데이터가 브라우저에 로드되었습니다!</strong>
                      실제 적용하려면 "설정 저장" 버튼을 클릭하세요.
                    </span>
                  </div>
                )}

                {/* ID와 URL 정보 */}
                <div
                  style={{
                    marginLeft: "20px",
                    marginBottom: "12px",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <strong style={{ color: "#92400e" }}>ID:</strong>
                    <span
                      style={{
                        background: "#f59e0b",
                        color: "white",
                        padding: "2px 8px",
                        borderRadius: "4px",
                        fontFamily: "monospace",
                        fontSize: "13px",
                        fontWeight: "600",
                      }}
                    >
                      {currentTVData.name}
                    </span>
                  </div>

                  <span style={{ color: "#92400e" }}>|</span>

                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <strong style={{ color: "#92400e" }}>URL:</strong>
                    <a
                      href={`/${currentTVData.name}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1976d2",
                        textDecoration: "none",
                        fontSize: "12px",
                        background: "#dbeafe",
                        padding: "2px 6px",
                        borderRadius: "4px",
                        border: "1px solid #93c5fd",
                        fontWeight: "500",
                      }}
                      onMouseOver={(e) => {
                        e.target.style.background = "#93c5fd";
                        e.target.style.color = "white";
                      }}
                      onMouseOut={(e) => {
                        e.target.style.background = "#dbeafe";
                        e.target.style.color = "#1976d2";
                      }}
                    >
                      {window.location.origin}/{currentTVData.name}
                    </a>
                  </div>
                </div>

                {/* 버튼들 */}
                <div
                  style={{
                    marginLeft: "20px",
                    display: "flex",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  {/* 수정된 샘플 데이터 로더 - onSampleDataLoad prop 추가 */}
                  <SampleDataLoader
                    tvId={selectedTVId}
                    tvName={currentTVData.name}
                    currentTVData={currentTVData}
                    onSampleDataLoad={handleSampleDataLoad}
                    disabled={!currentTVData.enabled}
                  />

                  <button
                    onClick={() =>
                      window.open(`/${currentTVData.name}`, "_blank")
                    }
                    style={{
                      background: "linear-gradient(45deg, #4dabf7, #339af0)",
                      color: "white",
                      border: "none",
                      borderRadius: "20px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 8px rgba(77, 171, 247, 0.3)",
                    }}
                    onMouseOver={(e) => {
                      e.target.style.transform = "translateY(-1px)";
                      e.target.style.boxShadow =
                        "0 4px 12px rgba(77, 171, 247, 0.4)";
                    }}
                    onMouseOut={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow =
                        "0 2px 8px rgba(77, 171, 247, 0.3)";
                    }}
                  >
                    🎬 현황판 미리보기
                  </button>

                  {/* 수정된 설정 저장 버튼 - 통일된 텍스트와 스타일 */}
                  <button
                    onClick={handleSaveSettings}
                    disabled={isSavingAll || updateTVMutation.isPending}
                    style={{
                      background: "linear-gradient(45deg, #00b894, #00a085)",
                      color: "white",
                      border: "none",
                      borderRadius: "20px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor:
                        isSavingAll || updateTVMutation.isPending
                          ? "wait"
                          : "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 8px rgba(0, 184, 148, 0.3)",
                      opacity:
                        isSavingAll || updateTVMutation.isPending ? 0.7 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (!isSavingAll && !updateTVMutation.isPending) {
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(0, 184, 148, 0.4)";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isSavingAll && !updateTVMutation.isPending) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 2px 8px rgba(0, 184, 148, 0.3)";
                      }
                    }}
                  >
                    💾{" "}
                    {isSavingAll || updateTVMutation.isPending
                      ? "저장 중..."
                      : "설정 저장"}
                  </button>

                  {/* 데이터 초기화 버튼 */}
                  <button
                    onClick={handleDataClear}
                    disabled={isClearingData || updateTVMutation.isPending}
                    style={{
                      background: "linear-gradient(45deg, #e17055, #d63031)",
                      color: "white",
                      border: "none",
                      borderRadius: "20px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor:
                        isClearingData || updateTVMutation.isPending
                          ? "wait"
                          : "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 8px rgba(225, 112, 85, 0.3)",
                      opacity:
                        isClearingData || updateTVMutation.isPending ? 0.7 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (!isClearingData && !updateTVMutation.isPending) {
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(225, 112, 85, 0.4)";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isClearingData && !updateTVMutation.isPending) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 2px 8px rgba(225, 112, 85, 0.3)";
                      }
                    }}
                  >
                    🗑️ {isClearingData ? "초기화 중..." : "데이터 초기화"}
                  </button>

                  {/* 현황판 삭제 버튼 추가 */}
                  <button
                    onClick={handleDeleteTV}
                    disabled={isDeletingTV || deleteTVMutation.isPending}
                    style={{
                      background: "linear-gradient(45deg, #dc2626, #b91c1c)",
                      color: "white",
                      border: "none",
                      borderRadius: "20px",
                      padding: "6px 14px",
                      fontSize: "12px",
                      fontWeight: "500",
                      cursor:
                        isDeletingTV || deleteTVMutation.isPending
                          ? "wait"
                          : "pointer",
                      transition: "all 0.2s",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: "0 2px 8px rgba(220, 38, 38, 0.3)",
                      opacity:
                        isDeletingTV || deleteTVMutation.isPending ? 0.7 : 1,
                    }}
                    onMouseOver={(e) => {
                      if (!isDeletingTV && !deleteTVMutation.isPending) {
                        e.target.style.transform = "translateY(-1px)";
                        e.target.style.boxShadow =
                          "0 4px 12px rgba(220, 38, 38, 0.4)";
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!isDeletingTV && !deleteTVMutation.isPending) {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 2px 8px rgba(220, 38, 38, 0.3)";
                      }
                    }}
                  >
                    ❌{" "}
                    {isDeletingTV || deleteTVMutation.isPending
                      ? "삭제 중..."
                      : "현황판 삭제"}
                  </button>
                </div>

                {/* 상태 표시 (비활성화된 경우) */}
                {!currentTVData.enabled && (
                  <div
                    style={{
                      marginTop: "10px",
                      marginLeft: "20px",
                      padding: "6px 10px",
                      background: "#fef3c7",
                      border: "1px solid #f59e0b",
                      borderRadius: "6px",
                      fontSize: "11px",
                      color: "#92400e",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <span>⚠️</span>
                    <span>
                      이 현황판은 현재 비활성화 상태입니다. 활성화하면 TV에서
                      접근할 수 있습니다.
                    </span>
                  </div>
                )}
              </div>

              {/* 수정된 PIN 인증 관리 - 콜백 추가 */}
              {typeof PinManagement === "function" && (
                <div style={{ marginBottom: "20px" }}>
                  <PinManagement
                    tvId={selectedTVId}
                    pinSettings={currentPinSettings}
                    onRefresh={() => fetchTVData(selectedTVId)}
                    onPinSettingsChange={handlePinSettingsChange}
                  />
                </div>
              )}

              {/* 수정된 음악 관리 - toast 함수들 추가 */}
              {typeof MusicManagement === "function" && (
                <div style={{ marginBottom: "20px" }}>
                  <MusicManagement
                    tvId={selectedTVId}
                    music={currentMusic}
                    bgmSettings={bgmSettings}
                    onRefresh={() => fetchTVData(selectedTVId)}
                    onBgmSettingsChange={handleBgmSettingsChange}
                    onMusicChange={handleMusicChange}
                    toastSuccess={toastSuccess}
                    toastError={toastError}
                  />
                </div>
              )}

              {/* 수정된 컨텐츠 관리 - toast 함수들 추가 */}
              {typeof ContentManagement === "function" && (
                <div style={{ marginBottom: "20px" }}>
                  <ContentManagement
                    tvId={selectedTVId}
                    contents={currentContents}
                    onRefresh={() => fetchTVData(selectedTVId)}
                    onContentsChange={handleContentsChange}
                    toastSuccess={toastSuccess}
                    toastError={toastError}
                  />
                </div>
              )}

              {/* 수정된 설정 관리 - tvData prop 추가 */}
              {typeof SettingsManagement === "function" && (
                <div style={{ marginBottom: "20px" }}>
                  <SettingsManagement
                    tvId={selectedTVId}
                    settings={currentSettings}
                    onRefresh={() => fetchTVData(selectedTVId)}
                    onSettingsChange={handleSettingsChange}
                    tvData={currentTVData}
                  />
                </div>
              )}
            </>
          )}

          {/* TV가 선택되지 않았을 때 */}
          {!selectedTVId && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                background: "#f8f9fa",
                borderRadius: "8px",
                border: "2px dashed #dee2e6",
              }}
            >
              <div
                style={{
                  fontSize: "3em",
                  marginBottom: "15px",
                  opacity: 0.6,
                }}
              >
                📺
              </div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "18px",
                  color: "#495057",
                  fontWeight: "600",
                }}
              >
                현황판을 선택해주세요
              </h4>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  color: "#6c757d",
                }}
              >
                위에서 현황판을 선택하면 해당 현황판의 컨텐츠를 관리할 수
                있습니다.
              </p>
            </div>
          )}

          {/* 로딩 중일 때 */}
          {selectedTVId && isLoadingTVData && (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                background: "#f8f9fa",
                borderRadius: "8px",
                border: "2px dashed #dee2e6",
              }}
            >
              <div
                style={{
                  fontSize: "2em",
                  marginBottom: "15px",
                  opacity: 0.6,
                }}
              >
                ⏳
              </div>
              <h4
                style={{
                  margin: "0 0 8px 0",
                  fontSize: "18px",
                  color: "#495057",
                  fontWeight: "600",
                }}
              >
                현황판 정보를 불러오는 중...
              </h4>
            </div>
          )}
        </div>
      </div>

      {/* 로그아웃 확인 모달 */}
      <ConfirmationModal
        isOpen={showLogoutModal}
        message="로그아웃 하시겠습니까?"
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
        isDelete={false}
      />

      {/* 활성화 상태 변경 확인 모달 */}
      <ConfirmationModal
        isOpen={showEnableModal}
        message={
          pendingEnabledState
            ? `현황판을 활성화하시겠습니까?\n\n• 활성화하는 경우, 현황판에서 재생이 가능합니다.`
            : `현황판을 비활성화하시겠습니까?\n\n• 비활성화하는 경우, 재생중인 현황판이 중단됩니다.`
        }
        onConfirm={handleConfirmToggleEnabled}
        onCancel={handleCancelToggleEnabled}
        isDelete={false}
      />

      {/* 데이터 초기화 확인 모달 */}
      <ConfirmationModal
        isOpen={showDataClearModal}
        message={`데이터를 정말 초기화하시겠습니까?\n\n• 초기화하면 기존 컨텐츠와 배경음악이 모두 삭제됩니다.\n• PIN 설정과 전환 효과 설정은 유지됩니다.\n• 이 작업은 되돌릴 수 없습니다.`}
        onConfirm={handleConfirmDataClear}
        onCancel={handleCancelDataClear}
        isDelete={true}
      />

      {/* 현황판 삭제 확인 모달 */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        message={`현재 선택된 현황판을 정말 삭제하시겠습니까?\n\n• 현황판: ${
          currentTVData?.name || currentTVData?.id
        }\n• 삭제하면 모든 컨텐츠, 설정, 음악이 완전히 제거됩니다.\n• 이 작업은 되돌릴 수 없습니다.`}
        onConfirm={handleConfirmDeleteTV}
        onCancel={handleCancelDeleteTV}
        isDelete={true}
      />
    </div>
  );
};

export default AdminPanel;
